import { useEffect, useRef } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChannelNotifications')

/**
 * useChannelNotifications - Manages desktop notifications for new channel messages
 * 
 * Detects when unread counts increase and shows desktop notifications for channels
 * that are not currently active. Only works when notifications are granted and user
 * is authenticated.
 * 
 * @param {Array} channels - Current channels with unread counts
 * @param {Object} activeChannel - Currently active channel
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {boolean} isAdmin - Whether user is admin
 * @param {Object} user - Current user object
 * @param {Function} t - Translation function
 * 
 * @atomic - <100 lines, single side-effect purpose
 * @idempotent - Same unread changes always produce same notifications
 */
export function useChannelNotifications(
  channels,
  activeChannel,
  isAuthenticated,
  isAdmin,
  user,
  t
) {
  const prevChannelsRef = useRef(new Map())

  useEffect(() => {
    // Only process notifications for authenticated users with permission granted
    if (!isAuthenticated || !('Notification' in window) || Notification.permission !== 'granted') {
      return
    }

    const oldChannelsMap = prevChannelsRef.current
    
    channels.forEach(ch => {
      const oldUnread = oldChannelsMap.get(ch.id) || 0
      const newUnread = ch.unreadCount || 0
      
      // Show notification if unread count increased and not viewing this channel
      if (newUnread > oldUnread && activeChannel?.id !== ch.id) {
        // Get display name for notification
        let displayName = ch.name
        if (isAdmin && ch.channelType === 'default' && ch.ownerEmail && ch.ownerEmail !== user?.email) {
          displayName = `${ch.ownerFirstName} ${ch.ownerLastName}`
        } else if (ch.name === 'TrckIt Support') {
          displayName = t('channels.supportChannelName')
        }
        
        const notification = new Notification('TrckIt - New Message', {
          body: `You have ${newUnread} unread message${newUnread > 1 ? 's' : ''} in ${displayName}`,
          icon: '/favicon.ico',
          tag: `channel-${ch.id}`, // Prevent duplicate notifications
          requireInteraction: false
        })
        
        notification.onclick = () => {
          window.focus()
          // Note: We can't call setActiveChannel here as it's not passed to this hook
          // The notification just brings focus to the window
          notification.close()
        }
        
        logger.info('Desktop notification shown', {
          channelId: ch.id,
          channelName: displayName,
          unreadCount: newUnread
        })
      }
    })
    
    // Update reference for next comparison
    prevChannelsRef.current = new Map(channels.map(ch => [ch.id, ch.unreadCount || 0]))
  }, [channels, activeChannel?.id, isAuthenticated, isAdmin, user?.email, t])
}
